import React, { useMemo } from "react";
import {
    GridRenderEditCellParams,
    GridSingleSelectColDef,
    useGridApiContext,
} from "@mui/x-data-grid-premium";
import { AutocompleteOption, AutocompleteWrapper } from "../muiAutocomplete";
import { useTranslation } from "react-i18next";

/**
 * Generic autocomplete cell for MuiEditableDataGrid.
 * Opens the dropdown immediately on focus and saves the selected value
 * back to the grid via the DataGrid API.
 * Options are read from colDef.valueOptions (same format as singleSelect columns).
 */
export const DataGridAutocompleteCell = ({
    id,
    field,
    value,
    hasFocus,
    colDef,
    onValueChange,
}: GridRenderEditCellParams & { onValueChange?: (apiRef: ReturnType<typeof useGridApiContext>, id: string | number, value: string | null, option: AutocompleteOption | null) => void }) => {
    const apiRef = useGridApiContext();
    const { valueOptions } = colDef as GridSingleSelectColDef;
    const { t } = useTranslation();

    const options: AutocompleteOption[] = useMemo(
        () =>
            ((valueOptions as { value: string; label: string; group?: string }[]) ?? []).map(
                (o) => ({ id: o.value, title: o.label, group: o.group })
            ),
        [valueOptions]
    );

    const hasGroups = options.some((o) => !!o.group);

    const handleSelect = (opt: AutocompleteOption | null) => {
        void apiRef.current.setEditCellValue({ id, field, value: opt?.id ?? null });
        onValueChange?.(apiRef, id, opt?.id ?? null, opt);
    };

    return (
        <AutocompleteWrapper
            options={options}
            size="small"
            defaultOpen={hasFocus}
            value={value}
            placeholder={t("common.typeToSeeOptions")}
            onChange={(val: AutocompleteOption) => handleSelect(val ?? null)}
            groupBy={hasGroups ? (o) => o.group : undefined}
            fullWidth
            textFieldProps={{
                size: "small",
                variant: "standard",
                sx: { fontSize: "0.875rem" },
            }}
        />
    );
};
